// A-0008.03: Classic (V1) opcode UI removed. Single list of V2+ opcodes;
// each opcode can be edited via Graphical or JSON builder (both edit the
// same Redis doc at `opcode:{org}:{sub}:{id}`).
import {
    useCallback,
    useEffect, useMemo, useRef, useState
} from 'react';
import {
    PageContent, PageContentHeader, PageContentHeaderText, PageContentHeaderTitle, PageDescription, PageHeader, PageHeaderAction, PageLayout, PageSubTitle, PageTitle, PageTitles
} from 'shared/components/NewDesignComponents/PageLayout';
import {
    Button
} from 'shared/components/ui/button';
import V2OpCodeBuilderCard from './v2/V2OpCodeBuilderCard';
import SearchItems from 'shared/components/NewDesignComponents/SearchItems';
import {
    BetaFeatureModal
} from '../components/BetaFeatureModel';
import {
    useParams,
    useNavigate
} from 'react-router-dom';
import ItemsSortComponent from 'shared/components/ItemsSortComponent';
import {
    jsonOpcodeService
} from 'shared/api/services/BEX/jsonOpcode.service';
import {
    AppLoader
} from 'shared/components/LoaderSpinner';

const OpCodeBuilder = () => {
    const [show, setShow] = useState(false);
    const [sortBy, setSortBy] = useState('desc');
    const {
        oragID, subOragID
    } = useParams();
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [jsonOpcodes, setJsonOpcodes] = useState([]);
    const [isJsonOpcodeLoading, setIsJsonOpcodeLoading] = useState(false);
    const navigate = useNavigate();

    const typingTimer = useRef(null);

    useEffect(() => {
        clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim());
        }, 1000);

        return () => clearTimeout(typingTimer.current);
    }, [searchTerm]);

    const fetchJSONOpcodes = useCallback(async () => {
        if (!oragID || !subOragID) return;
        try {
            setIsJsonOpcodeLoading(true);
            const response = await jsonOpcodeService.listJSONOpcodes({
                organizationId: oragID,
                subOrganizationId: subOragID
            });
            setJsonOpcodes(response.data || []);
        } catch (error) {
            console.error('Error fetching opcodes:', error);
            setJsonOpcodes([]);
        } finally {
            setIsJsonOpcodeLoading(false);
        }
    }, [oragID, subOragID]);

    useEffect(() => {
        fetchJSONOpcodes();
    }, [debouncedSearch, fetchJSONOpcodes]);

    const handleDeleteJSONOpcode = useCallback((opcodeId) => {
        setJsonOpcodes((prev) => prev.filter((opcode) => {
            const currentId = opcode?.opcode_id || opcode?._id || opcode?.id;
            return currentId !== opcodeId;
        }));
    }, []);

    const getCreatedTime = (item) => {
        const fallback = item?.updated || item?.updatedAt || 0;
        const timestamp = item?.created || item?.createdAt || fallback;
        const timeValue = timestamp ? new Date(timestamp).getTime() : 0;
        return Number.isNaN(timeValue) ? 0 : timeValue;
    };

    const sortByCreatedTime = (items) => {
        if (!Array.isArray(items)) return [];
        return [...items].sort((a, b) => {
            const aTime = getCreatedTime(a);
            const bTime = getCreatedTime(b);
            return sortBy === 'asc' ? aTime - bTime : bTime - aTime;
        });
    };

    const filteredOpcodes = useMemo(() => {
        if (!searchTerm.trim()) return sortByCreatedTime(jsonOpcodes);
        const lowerSearch = searchTerm.toLowerCase();
        const filtered = jsonOpcodes.filter((opcode) =>
            opcode.id?.toLowerCase().includes(lowerSearch) ||
            opcode._id?.toLowerCase().includes(lowerSearch) ||
            opcode.opcode_id?.toLowerCase().includes(lowerSearch) ||
            opcode.title?.toLowerCase().includes(lowerSearch) ||
            opcode.name?.toLowerCase().includes(lowerSearch) ||
            opcode.description?.toLowerCase().includes(lowerSearch)
        );
        return sortByCreatedTime(filtered);
    }, [jsonOpcodes, searchTerm, sortBy]);

    return (
        <PageLayout>
            <PageHeader>
                <PageTitles>
                    <PageTitle>
                        Build your Enterprise
                    </PageTitle>
                    <PageSubTitle>
                        Workflow Blueprints
                    </PageSubTitle>
                    <PageDescription>
                        Design intelligent, scalable Opcodes with agentic AI and operational codes. Streamline execution, optimize processes, and ensure precision with customizable blueprints for enterprise success
                    </PageDescription>
                </PageTitles>
                <PageHeaderAction>
                    <Button onClick={() => setShow(true)} className='w-full'> Add new opcode </Button>
                </PageHeaderAction>
            </PageHeader>
            <PageContent>

                <PageContentHeader>
                    <PageContentHeaderTitle>Opcode Builder:</PageContentHeaderTitle>
                    <PageContentHeaderText>Manage your Agentic AI Operational Codes</PageContentHeaderText>
                </PageContentHeader>

                <div className="w-full mx-auto p-2 md:p-6">
                    <div className="space-y-4">
                        <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-4'>
                            <SearchItems items={jsonOpcodes} handleFilterItems={(value) => setSearchTerm(value)}/>
                            <div className="flex items-center gap-3">
                                <span className="text-sm text-muted-foreground whitespace-nowrap">Sort by:</span>
                                <ItemsSortComponent
                                    sortBy={sortBy}
                                    setSortBy={setSortBy}
                                />
                            </div>
                        </div>

                        {filteredOpcodes.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                                {filteredOpcodes.map((opCode) => (
                                    <V2OpCodeBuilderCard
                                        key={opCode?._id || opCode?.opcode_id}
                                        opCode={opCode}
                                        onManage={() => {
                                            const id = opCode?.opcode_id || opCode?._id;
                                            if (!oragID || !subOragID || !id) return;
                                            navigate(`/organizations/${oragID}/sub/${subOragID}/op-codes/v2/${id}`);
                                        }}
                                        onDelete={handleDeleteJSONOpcode}
                                    />
                                ))}
                            </div>
                        ) : !isJsonOpcodeLoading && (
                            <div className='flex flex-grow justify-center items-center py-8'>
                                <span className="text-sm text-muted-foreground">No opcodes found</span>
                            </div>
                        )}
                        {isJsonOpcodeLoading && (
                            <div className='flex justify-center items-center py-8'>
                                <AppLoader size={25} />
                            </div>
                        )}
                    </div>
                </div>
            </PageContent>

            {/* create new opcode dialog */}
            <BetaFeatureModal
                handleUseJson={() => {
                    if (!oragID || !subOragID) return;
                    navigate(`/organizations/${oragID}/sub/${subOragID}/op-codes/json/create`);
                }}
                handleUseGraphicalV2={() => {
                    if (!oragID || !subOragID) return;
                    navigate(`/organizations/${oragID}/sub/${subOragID}/op-codes/v2/create`);
                }}
                onOpenChange={setShow}
                open={show}
            />
        </PageLayout>
    );
};

export default OpCodeBuilder;
